import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Gift, Lock, Volume2, VolumeX, Play, Sparkles } from "lucide-react";
import { sendVideoEvent } from "../../lib/pageviewTracker";

// 2026-08 hero refresh: the 45s teaser is replaced by the 69s 8-page product
// tour ("What $10 actually buys"). Layout moved from side-by-side to stacked —
// headline block on top, video full-width beneath it (user request: bigger
// video under the title/headline). Old teaser slugs stay allow-listed.
const VIDEO_URL_WEBM = `${process.env.REACT_APP_BACKEND_URL}/api/public/landing-video/product_tour8_youtube.webm`;
const VIDEO_URL_MP4 = `${process.env.REACT_APP_BACKEND_URL}/api/public/landing-video/product_tour8_youtube`;

// ---------------------------------------------------------------------------
// AI CMO @Hook Writer delivery (2026-06-19) — 10 Avoider-targeted hooks for
// the landing sub-headline strip. Pinned per session (useState initializer)
// so re-renders don't reshuffle. Each variant gets a `?ref=hook-N` tag so the
// existing attribution funnel can split-test which lines actually carry the
// click-through.
// 2026-08 (user direction): every hook = persona strength + a NAMED piece
// of the system (Wealth Plan, Cash Flow Scan, Calendar, Spending Plan, Net
// Worth, projected balance, weekly check-in, Habit Loop), persona label at
// the end. `ref` values are STABLE ids — new copy always gets a NEW ref so
// hook analytics never conflate different lines (1-21 = retired copy).
const HOOK_LINES = [
  { ref: 22, persona: "Architect", copy: "You're a planner at heart. The 5-Year Wealth Plan puts your money on a schedule — and it starts behaving like the rest of your plans." },
  { ref: 23, persona: "Explorer", copy: "You love finding what others miss. The Cash Flow Scan sweeps your month with ten leak detectors and hands you the map." },
  { ref: 24, persona: "Guardian", copy: "You protect what matters. The Cash Flow Calendar shows every bill before it lands, so nothing catches your household off guard." },
  { ref: 25, persona: "Connector", copy: "You lead with heart. The Spending Plan gives every dollar a job, so there's always something left for the people you love." },
  { ref: 26, persona: "Dreamer", copy: "You can already see the future you want. The Net Worth tracker turns it into a line you can watch rise." },
  { ref: 27, persona: "Achiever", copy: "You play to win. Your balance, projected 30, 60, and 90 days ahead, is a scoreboard only you can beat." },
  { ref: 28, persona: "Minimalist", copy: "You keep things simple on purpose. One five-minute weekly check-in runs the whole system." },
  { ref: 29, persona: "Peacekeeper", copy: "You keep the peace everywhere you go. The Habit Loop keeps it with your money — the cue remembers, the routine runs, and nothing sneaks up on you." },
];

function pickHook() {
  return HOOK_LINES[Math.floor(Math.random() * HOOK_LINES.length)];
}

/**
 * Landing-page hero — left column carries the brand-anchored headline + CTAs,
 * right column plays the 45s product showcase teaser. Autoplays muted while
 * in view (IntersectionObserver pauses it off-screen); 30 accumulated watch
 * seconds opens the lead-capture modal. Quiz CTA sits BELOW the frame so it
 * never covers the burned-in captions.
 *
 * Watch tracking (one-shot per page load, fire-and-forget beacons):
 *   play → watch_10s → watch_30s → modal_open. Reported on
 *   /app/admin/metrics ("Hero video" tile) so we can tell whether the
 *   hero placement earns its spot, not just whether people click after it.
 */
export default function Hero() {
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  // Cumulative watched seconds across loops + scroll-pauses — raw
  // currentTime resets every loop, so we sum forward deltas instead.
  const watchedRef = useRef(0);
  const lastTimestampRef = useRef(null);
  // One-shot watch-beacon guards (per page load).
  const trackedRef = useRef({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  // Pin the hook rotation on first render so re-renders don't reshuffle.
  const [hook] = useState(pickHook);

  const track = (event) => {
    if (trackedRef.current[event]) return;
    trackedRef.current[event] = true;
    sendVideoEvent(event);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const onTimeUpdate = () => {
      const now = video.currentTime;
      const prev = lastTimestampRef.current;
      // Only count forward motion under 1 second (excludes seeks AND the
      // loop wraparound that fires at the same instant).
      if (prev !== null && now > prev && now - prev < 1.0) {
        watchedRef.current += now - prev;
        if (watchedRef.current >= 10) track("watch_10s");
        if (watchedRef.current >= 30) track("watch_30s");
      }
      lastTimestampRef.current = now;
    };
    const onSeekedOrLoop = () => {
      lastTimestampRef.current = video.currentTime;
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("seeked", onSeekedOrLoop);
    video.addEventListener("play", onSeekedOrLoop);
    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("seeked", onSeekedOrLoop);
      video.removeEventListener("play", onSeekedOrLoop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autoplay while the frame is in view; pause when scrolled away.
  useEffect(() => {
    const node = frameRef.current;
    const video = videoRef.current;
    if (!node || !video) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: [0, 0.4, 0.8] },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  const handleVideoClick = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  const handleToggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setIsMuted(next);
    if (!next && v.paused) {
      v.play().catch(() => {});
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 lg:pt-32 pb-20 md:pb-28">
      <div className="max-w-4xl mx-auto text-center">
        <p className="eyebrow text-[#C05C3D] mb-6" data-testid="hero-eyebrow">
          A habit loop for your money
        </p>
        <h1 className="text-5xl sm:text-6xl lg:text-6xl xl:text-7xl font-serif font-light leading-[1.05] tracking-tight">
          Build the <em className="ink-underline not-italic">financial habits</em> that build wealth.
        </h1>
        {/* AI CMO @Hook Writer — Avoider-targeted scroll-stopper. Pinned per
            session via useState initializer; ?ref=hook-N tag propagates to
            the existing /admin/metrics/attribution-funnel report so we can
            see which of the 10 hooks actually drives clicks. */}
        <p
          className="mt-6 text-lg sm:text-xl text-[#3E6B37] max-w-2xl mx-auto italic leading-relaxed"
          data-testid="hero-hook-strip"
          data-hook-ref={hook.ref}
        >
          {hook.copy}{" "}
          <span className="not-italic text-sm text-[#787671] whitespace-nowrap" data-testid="hero-hook-persona">
            ({hook.persona})
          </span>
        </p>
        {/* Quiz tease (user copy) — the link carries ?ref=hook-N so the
            attribution funnel can finally split-test which hook CONVERTS,
            not just which one renders. */}
        <p className="mt-3 text-sm text-[#4d4f4c]" data-testid="hero-hook-quiz-tease">
          Does this sound like you?{" "}
          <Link
            to={`/quiz?ref=hook-${hook.ref}`}
            className="font-medium text-[#C05C3D] underline underline-offset-4 hover:text-[#A34A2E] transition-colors"
            data-testid="hero-hook-quiz-tease-link"
          >
            Take the quiz and find out.
          </Link>
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/register"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[#C05C3D] text-white hover:bg-[#A34A2E] transition-colors"
            data-testid="hero-cta-primary"
          >
            Start 60 days free
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-[#2C302E] hover:bg-[#2C302E] hover:text-white transition-colors"
            data-testid="hero-cta-secondary"
          >
            See pricing
          </a>
          <div className="flex items-center gap-6 text-sm text-[#787671]">
            <div className="flex items-center gap-2 font-medium text-[#3E6B37]" data-testid="hero-trial-chip">
              <Gift className="h-4 w-4" /> No card needed
            </div>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4" /> Private by default
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4" /> Cancel anytime
            </div>
          </div>
        </div>
        <p className="mt-3 text-xs text-[#787671]" data-testid="hero-trial-subline">
          Full app free for your first 60 days. $10/month after, only if you stay.
        </p>
      </div>

      {/* Full-width tour video UNDER the headline block — the 69s 8-page
          product tour. Bigger canvas than the old side-by-side column. */}
      <div className="mt-12 md:mt-16 max-w-5xl mx-auto">
        <div
          ref={frameRef}
          className="relative group bg-[#1B1E1C] rounded-sm overflow-hidden border border-[#E6E3DB] shadow-md"
          data-testid="hero-video-frame"
        >
          <button
            type="button"
            onClick={handleVideoClick}
            className="block w-full aspect-video relative cursor-pointer"
            aria-label={isPlaying ? "Pause demo video" : "Play demo video"}
            data-testid="hero-video-play-button"
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
              onPlay={() => { setIsPlaying(true); track("play"); }}
              onPause={() => setIsPlaying(false)}
              aria-label="One-minute tour of all eight Finhabits pages"
              data-testid="hero-video"
            >
              {/* WebM first: far smaller than the H.264 MP4. Browsers pick
                  the first <source> they can decode. */}
              <source src={VIDEO_URL_WEBM} type="video/webm" />
              <source src={VIDEO_URL_MP4} type="video/mp4" />
            </video>
            {/* Center play hint — fades out while playing, fades back when paused. */}
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none ${
                isPlaying ? "opacity-0" : "opacity-100"
              }`}
              data-testid="hero-video-play-hint"
            >
              <div className="bg-white/95 text-[#2C302E] rounded-full h-16 w-16 flex items-center justify-center shadow-lg">
                <Play className="h-6 w-6 ml-1" fill="currentColor" />
              </div>
            </div>
          </button>
          {/* Speaker toggle — top-right; the tour's caption chips sit at the
              bottom of the frame, so the corner stays clear. */}
          <button
            type="button"
            onClick={handleToggleMute}
            className="absolute top-3 right-3 h-10 w-10 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center backdrop-blur-sm transition-colors z-10"
            aria-label={isMuted ? "Unmute video" : "Mute video"}
            data-testid="hero-video-mute-toggle"
          >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>

        {/* CTA bar BELOW the frame so it never covers the caption chips. */}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-[#787671]" data-testid="hero-video-caption">
            Watch · one minute — one money system, contained in eight pages.
          </p>
          <Link
            to="/quiz?ref=hero-video"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#C05C3D] text-white text-sm hover:bg-[#A34A2E] transition-colors whitespace-nowrap"
            data-testid="hero-video-cta"
          >
            <Sparkles className="h-4 w-4" />
            Take the free quiz
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
